import { trackClick } from 'js/utils/trackClick';

import type { ArmStamp, Mode } from './armRegistry';

export type AutotestStamp = ArmStamp & {
  autotest?: string;
  autotest_bucket: string;
  mode: Mode;
};

// Api::V0::ClicksController answers 400 extra_too_large and discards the whole
// click — source, bucket and entity id along with it — once the extra JSON
// reaches 256. Budgeted in BYTES rather than in the characters that check
// counts, because the warehouse column is a varchar(256) whose COPY runs
// TRUNCATECOLUMNS: an ordinary 90-character Japanese title is 101 characters of
// JSON, which the server accepts, and 282 bytes, which Redshift then chops
// mid-string into a clicks.extra nothing can parse. Bytes are the stricter of
// the two rulers, so one number covers both failures.
const EXTRA_JSON_BUDGET = 255;

// Shed order for the harness's own keys. render_state goes first because it is
// already optional — an arm with no stamp hook never sets one, so the analysis
// already has to handle rows without it. mode is on every click of every arm,
// and a column present on most rows and absent on the over-budget ones makes a
// GROUP BY mode undercount exactly the arms that send the most. autotest and
// autotest_bucket are never shed: they are what makes a row this test's row.
const AUX_DROP_ORDER: ReadonlyArray<keyof AutotestStamp> = [
  'render_state',
  'mode',
];

// public.clicks.source is varchar(64) and the COPY runs TRUNCATECOLUMNS, so a
// longer name is not an error, it is two arms' events silently merging into one
// indistinguishable row.
const SOURCE_LIMIT = 64;

// The server measures params[:extra].to_json, and ActiveSupport's
// escape_html_entities_in_json turns &, < and > — plus the two line separators
// JSON.stringify emits raw — into six-character \uXXXX escapes. Substituting a
// six-byte ASCII stand-in for each is what makes JSON.stringify's output
// measurable as the string Rails is going to build out of the same value.
const ESCAPED_BY_RAILS = /[<>&\u2028\u2029]/g;

// serverLength never reaches the six-byte branch below: JSON.stringify has been
// well-formed since ES2019, so a lone surrogate — what an arm gets by slicing a
// title mid-emoji — is already the six ASCII characters \udXXX by the time this
// counts it, arriving at the same six. The branch stays because it is what the
// escape costs on the wire and because this measures strings, not only
// stringify output.
//
// Nothing here may throw, and a bad title is the temptation. ctx.track runs
// inside arm-owned click listeners with no containment of their own, so a
// measurement that raised once would take out that arm's handler for the rest
// of the pageview.
function utf8Length(text: string): number {
  let bytes = 0;

  for (let i = 0; i < text.length; i += 1) {
    const code = text.charCodeAt(i);

    if (code < 0x80) {
      bytes += 1;
    } else if (code < 0x800) {
      bytes += 2;
    } else if (code < 0xd800 || code > 0xdfff) {
      bytes += 3;
    } else if (code < 0xdc00 && (text.charCodeAt(i + 1) & 0xfc00) === 0xdc00) {
      bytes += 4;
      i += 1;
    } else {
      bytes += 6;
    }
  }

  return bytes;
}

function serverLength(value: object): number {
  return utf8Length(JSON.stringify(value).replace(ESCAPED_BY_RAILS, '\\u0000'));
}

// One pass of the ladder: the candidate with as many aux keys shed as it takes,
// or null when even the stripped object is too heavy. Mutates what it is given,
// so every caller hands it a fresh spread — a delete that reached the harness's
// own stamp object would cost every LATER click of the pageview the same key.
function fitted(
  candidate: Record<string, unknown>,
): Record<string, unknown> | null {
  for (const auxKey of AUX_DROP_ORDER) {
    if (serverLength(candidate) <= EXTRA_JSON_BUDGET) return candidate;
    delete candidate[auxKey];
  }

  return serverLength(candidate) <= EXTRA_JSON_BUDGET ? candidate : null;
}

// The degradation ladder, first rung that measures under budget wins:
//
//   1  the arm's extra plus the whole stamp
//   2  ...minus render_state   } AUX_DROP_ORDER
//   3  ...minus mode           }
//   4  the stamp alone, whole again
//   5  ...minus render_state
//   6  ...minus mode
//   7  {}
//
// The arm's own keys go as a SET rather than one at a time: only the arm knows
// which of them was worth sending, and a harness picking for it would be
// guessing. They go below both aux keys because an over-budget payload is the
// arm's own doing almost by definition, and buying render_state and mode back
// out of the room that frees (rung 4) beats keeping half an arm payload.
//
// Rung 7 is the postcondition rather than a fallback: {} weighs two bytes, so
// what comes back is at or under budget on every path out of here. An extra
// lost whole still leaves an attributable click — autotest_bucket rides the
// clicks.bucket column, not this JSON — where an over-budget one is answered
// 400, dropped entirely, and counted as a bad_args statsd increment.
export function stampedExtra(
  baseExtra: object | undefined,
  stamp: AutotestStamp,
): object {
  return fitted({ ...baseExtra, ...stamp }) ?? fitted({ ...stamp }) ?? {};
}

// The one click-tracking entry point for this autotest. Arms emit specific
// events on the elements they own; nothing here binds a listener of its own.
export function trackArmClick(
  source: string,
  stamp: AutotestStamp,
  options: { entityId?: number | null; extra?: object } = {},
): void {
  trackClick({
    source: source.slice(0, SOURCE_LIMIT),
    bucket: stamp.autotest_bucket,
    entityId: options.entityId,
    extra: stampedExtra(options.extra, stamp),
  });
}
